import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  Filter,
  Loader2,
  MapPin,
  Play,
  Plus,
  RefreshCw,
  Save,
  Syringe,
  Target,
  Trash2,
  Users,
  X,
  Zap
} from 'lucide-react';
import { API_BASE_URL } from '../config';
import secureStorage from '../utils/secureStorage';

const POLL_INTERVAL = 60000; // 60 seconds

const ExConDashboard = ({ onBack, onNavigateToBuilder }) => {
  const [exercises, setExercises] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedExercise, setExpandedExercise] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Alert state for upcoming injections
  const [injectionAlert, setInjectionAlert] = useState(null);
  const [acknowledgedAlerts, setAcknowledgedAlerts] = useState(new Set());

  const getAuthHeaders = () => {
    const token = secureStorage.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [exercisesRes, summaryRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/excon-exercises`, { headers }),
        axios.get(`${API_BASE_URL}/api/excon-exercises/summary`, { headers })
      ]);
      setExercises(exercisesRes.data);
      setSummary(summaryRes.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching ExCon data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Check for upcoming injections every 30 seconds
  useEffect(() => {
    const checkUpcomingInjections = () => {
      if (injectionAlert) return; // Don't overwrite an active alert
      const now = new Date();
      const tenMinsFromNow = new Date(now.getTime() + 10 * 60 * 1000);

      for (const exercise of exercises) {
        const injections = exercise.injections || [];
        for (const inj of injections) {
          if (!inj.scenario_time || inj.status === 'completed') continue;
          if (acknowledgedAlerts.has(inj.id)) continue;

          const injTime = new Date(inj.scenario_time);
          if (isNaN(injTime.getTime())) continue;

          // Alert if injection is within the next 10 minutes and hasn't passed
          if (injTime > now && injTime <= tenMinsFromNow) {
            setInjectionAlert({
              injectionId: inj.id,
              exerciseName: exercise.exercise_name,
              scenarioTime: inj.scenario_time,
              eventType: inj.event_type || 'N/A',
              from: inj.from_entity || 'N/A',
              to: inj.to_entity || 'N/A',
              message: inj.message || '',
              minutesUntil: Math.ceil((injTime - now) / 60000),
            });
            return; // Show one alert at a time
          }
        }
      }
    };

    checkUpcomingInjections();
    const alertInterval = setInterval(checkUpcomingInjections, 30000);
    return () => clearInterval(alertInterval);
  }, [exercises, injectionAlert, acknowledgedAlerts]);

  const acknowledgeAlert = () => {
    if (injectionAlert) {
      setAcknowledgedAlerts(prev => new Set([...prev, injectionAlert.injectionId]));
    }
    setInjectionAlert(null);
  };

  const formatScenarioDateTime = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    if (isNaN(d.getTime())) return dt;
    return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const updateInjectionStatus = async (exerciseId, injectionId, newStatus) => {
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      await axios.put(
        `${API_BASE_URL}/api/exercise-builder/${exerciseId}/injection/${injectionId}`,
        { status: newStatus },
        { headers }
      );
      fetchData();
    } catch (error) {
      console.error('Error updating injection:', error);
    }
  };

  const navigateToStep8 = (exerciseId) => {
    if (onNavigateToBuilder) {
      onNavigateToBuilder(exerciseId, 8);
    } else {
      window.location.hash = `#builder?exercise=${exerciseId}&step=8&returnTo=excon-dashboard`;
    }
  };

  const filteredExercises = exercises.filter(ex => {
    if (filter === 'all') return true;
    return ex.status === filter;
  });

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400 border-green-500/40',
      completed: 'bg-blue-500/20 text-blue-400 border-blue-500/40',
      ready: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
      draft: 'bg-gray-500/20 text-gray-400 border-gray-500/40'
    };
    return (
      <Badge className={`${styles[status] || styles.draft} border`} data-testid={`status-badge-${status}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getPhaseBadge = (phase) => {
    const colors = {
      Prevention: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      Mitigation: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      Preparedness: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      Response: 'bg-red-500/20 text-red-300 border-red-500/30',
      Recovery: 'bg-green-500/20 text-green-300 border-green-500/30'
    };
    return (
      <Badge className={`${colors[phase] || colors.Prevention} border text-xs`}>
        {phase}
      </Badge>
    );
  };

  const getInjectionStatusIcon = (status) => {
    if (status === 'sent') return <Clock className="h-4 w-4 text-yellow-400" />;
    if (status === 'completed') return <CheckCircle2 className="h-4 w-4 text-green-400" />;
    return <Clock className="h-4 w-4 text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading ExCon Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid="excon-dashboard">
      {/* Injection Alert Modal */}
      {injectionAlert && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10002]" data-testid="injection-alert-overlay">
          <div className="bg-gray-800 border-2 border-orange-500 rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" data-testid="injection-alert-modal">
            <div className="bg-orange-500/20 border-b border-orange-500/40 px-6 py-4 flex items-center space-x-3">
              <AlertTriangle className="h-6 w-6 text-orange-400 shrink-0" />
              <div>
                <h3 className="text-lg font-bold text-orange-400">Upcoming Injection Alert</h3>
                <p className="text-sm text-orange-300/70">{injectionAlert.exerciseName}</p>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-white text-sm">
                An injection is scheduled in approximately <span className="font-bold text-orange-400">{injectionAlert.minutesUntil} minute{injectionAlert.minutesUntil !== 1 ? 's' : ''}</span>.
              </p>
              <div className="bg-gray-700/50 rounded-lg p-4 space-y-3 border border-gray-600/50">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400 uppercase tracking-wide">Scenario Date/Time</span>
                  <span className="text-sm text-white font-medium">{formatScenarioDateTime(injectionAlert.scenarioTime)}</span>
                </div>
                <div className="border-t border-gray-600/50" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400 uppercase tracking-wide">Event Type</span>
                  <Badge className="bg-orange-500/20 text-orange-300 border border-orange-500/30 text-xs">{injectionAlert.eventType}</Badge>
                </div>
                <div className="border-t border-gray-600/50" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400 uppercase tracking-wide">From</span>
                  <span className="text-sm text-white">{injectionAlert.from}</span>
                </div>
                <div className="border-t border-gray-600/50" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400 uppercase tracking-wide">To</span>
                  <span className="text-sm text-white">{injectionAlert.to}</span>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-700 flex justify-center">
              <Button
                onClick={acknowledgeAlert}
                className="bg-orange-500 hover:bg-orange-600 text-black font-semibold px-8"
                data-testid="injection-alert-acknowledge-btn"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Acknowledge
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gray-800/80 border-b border-orange-500/20 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={onBack} className="text-gray-400 hover:text-white" data-testid="back-to-hira-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to HIRA
            </Button>
            <div className="h-6 w-px bg-gray-700" />
            <div>
              <h1 className="text-2xl font-bold text-orange-500">ExCon Dashboard</h1>
              <p className="text-sm text-gray-400">Exercise Control - Monitor & Manage Injections</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-xs text-gray-500">
              Last refresh: {lastRefresh.toLocaleTimeString()}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              className="border-orange-500/40 text-orange-400 hover:bg-orange-500/10"
              data-testid="refresh-btn"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="summary-cards">
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Total Exercises</p>
                    <p className="text-3xl font-bold text-white mt-1">{summary.total_exercises}</p>
                  </div>
                  <div className="bg-orange-500/20 p-3 rounded-lg">
                    <FileText className="h-6 w-6 text-orange-400" />
                  </div>
                </div>
                <div className="flex space-x-3 mt-3 text-xs">
                  <span className="text-green-400">{summary.active_exercises} active</span>
                  <span className="text-blue-400">{summary.completed_exercises} done</span>
                  <span className="text-gray-400">{summary.draft_exercises} draft</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Total Injections</p>
                    <p className="text-3xl font-bold text-white mt-1">{summary.total_injections}</p>
                  </div>
                  <div className="bg-purple-500/20 p-3 rounded-lg">
                    <Syringe className="h-6 w-6 text-purple-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Pending</p>
                    <p className="text-3xl font-bold text-yellow-400 mt-1">{summary.pending_injections}</p>
                  </div>
                  <div className="bg-yellow-500/20 p-3 rounded-lg">
                    <Clock className="h-6 w-6 text-yellow-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Done</p>
                    <p className="text-3xl font-bold text-green-400 mt-1">{summary.completed_injections}</p>
                  </div>
                  <div className="bg-green-500/20 p-3 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-green-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex items-center space-x-2 mb-6" data-testid="filter-tabs">
          <Filter className="h-4 w-4 text-gray-400" />
          {['all', 'active', 'ready', 'draft', 'completed'].map(f => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f
                ? 'bg-orange-500 hover:bg-orange-600 text-white'
                : 'border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-white'
              }
              data-testid={`filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'all' && exercises.length > 0 && ` (${exercises.length})`}
            </Button>
          ))}
        </div>

        {/* Empty State */}
        {filteredExercises.length === 0 && (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-12 text-center">
              <Syringe className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-300 mb-2">
                {filter === 'all' ? 'No ExCon Exercises Yet' : `No ${filter} exercises`}
              </h3>
              <p className="text-gray-500 text-sm">
                Create an ExCon exercise from the HIRA view to get started.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Exercise Cards */}
        <div className="space-y-4">
          {filteredExercises.map(exercise => {
            const isExpanded = expandedExercise === exercise.id;
            const injSummary = exercise.injection_summary;
            const progressPct = injSummary.total > 0
              ? Math.round(((injSummary.sent + injSummary.completed) / injSummary.total) * 100)
              : 0;

            return (
              <Card key={exercise.id} className="bg-gray-800 border-gray-700 overflow-hidden" data-testid={`exercise-card-${exercise.id}`}>
                {/* Exercise Header Row */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-750 transition-colors"
                  onClick={() => setExpandedExercise(isExpanded ? null : exercise.id)}
                  data-testid={`exercise-toggle-${exercise.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      <div className="bg-orange-500/20 p-2.5 rounded-lg shrink-0">
                        <Target className="h-5 w-5 text-orange-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-semibold text-white truncate">{exercise.exercise_name}</h3>
                          {getStatusBadge(exercise.status)}
                          {getPhaseBadge(exercise.current_emergency_phase)}
                        </div>
                        <div className="flex items-center space-x-4 text-xs text-gray-400">
                          <span className="flex items-center">
                            <FileText className="h-3 w-3 mr-1" />
                            {exercise.exercise_type}
                          </span>
                          {exercise.location && (
                            <span className="flex items-center">
                              <MapPin className="h-3 w-3 mr-1" />
                              {exercise.location}
                            </span>
                          )}
                          <span className="flex items-center">
                            <Syringe className="h-3 w-3 mr-1" />
                            {injSummary.total} injections
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 shrink-0">
                      {/* Progress bar */}
                      {injSummary.total > 0 && (
                        <div className="w-32 hidden md:block">
                          <div className="flex justify-between text-xs text-gray-400 mb-1">
                            <span>Progress</span>
                            <span>{progressPct}%</span>
                          </div>
                          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{
                                width: `${progressPct}%`,
                                background: progressPct === 100
                                  ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                                  : 'linear-gradient(90deg, #f97316, #ea580c)'
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Injection quick stats */}
                      <div className="flex items-center space-x-2 text-xs">
                        <span className="text-gray-400 bg-gray-700/60 px-2 py-1 rounded">{injSummary.pending}P</span>
                        <span className="text-yellow-400 bg-yellow-500/10 px-2 py-1 rounded">{injSummary.sent}A</span>
                        <span className="text-green-400 bg-green-500/10 px-2 py-1 rounded">{injSummary.completed}D</span>
                      </div>

                      {/* Quick Launch ICS */}
                      <Button
                        size="sm"
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.hash = `#ics?exercise=${exercise.id}`;
                        }}
                        data-testid={`launch-ics-${exercise.id}`}
                      >
                        <Play className="h-3 w-3 mr-1" />
                        ICS
                      </Button>

                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Injection Panel */}
                {isExpanded && (
                  <div className="border-t border-gray-700 bg-gray-850" data-testid={`injection-panel-${exercise.id}`}>
                    {/* Injection Header */}
                    <div className="px-4 py-3 bg-gray-800/60 flex items-center justify-between border-b border-gray-700/50">
                      <div className="flex items-center space-x-2">
                        <Syringe className="h-4 w-4 text-orange-400" />
                        <h4 className="font-semibold text-orange-400 text-sm">
                          MSEL Injections ({exercise.injections?.length || 0})
                        </h4>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-orange-500/40 text-orange-400 hover:bg-orange-500/10"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigateToStep8(exercise.id);
                        }}
                        data-testid={`add-injection-btn-${exercise.id}`}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Injection
                      </Button>
                    </div>

                    {/* Injection List */}
                    <div className="divide-y divide-gray-700/50 max-h-96 overflow-y-auto">
                      {(!exercise.injections || exercise.injections.length === 0) ? (
                        <div className="px-4 py-8 text-center text-gray-500 text-sm">
                          <Syringe className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                          No injections yet. Add one to get started.
                        </div>
                      ) : (
                        [...exercise.injections]
                          .sort((a, b) => {
                            const timeA = a.scenario_time || '';
                            const timeB = b.scenario_time || '';
                            if (!timeA && !timeB) return 0;
                            if (!timeA) return 1;
                            if (!timeB) return -1;
                            return timeA.localeCompare(timeB);
                          })
                          .map((inj, idx) => {
                          const injStatus = inj.status || 'pending';
                          return (
                            <div
                              key={inj.id || idx}
                              className={`px-4 py-3 flex items-start space-x-3 hover:bg-gray-700/30 transition-colors ${
                                injStatus === 'completed' ? 'opacity-60' : ''
                              }`}
                              data-testid={`injection-row-${inj.id || idx}`}
                            >
                              {/* Status icon */}
                              <div className="pt-0.5 shrink-0">
                                {getInjectionStatusIcon(injStatus)}
                              </div>

                              {/* Injection number & time */}
                              <div className="w-28 shrink-0">
                                <span className="text-xs font-mono text-orange-400">#{inj.event_number || idx + 1}</span>
                                {inj.scenario_time && (
                                  <div className="text-xs text-gray-500">{formatScenarioDateTime(inj.scenario_time)}</div>
                                )}
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1">
                                  {inj.event_type && (
                                    <Badge className="bg-gray-700 text-gray-300 border-gray-600 text-xs border">
                                      {inj.event_type}
                                    </Badge>
                                  )}
                                  {inj.from_entity && inj.to_entity && (
                                    <span className="text-xs text-gray-500">
                                      {inj.from_entity} → {inj.to_entity}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-200 leading-snug">{inj.message}</p>
                                {inj.expected_response && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Expected: {inj.expected_response}
                                  </p>
                                )}
                                {inj.actual_response && (
                                  <p className="text-xs text-green-400/70 mt-1">
                                    Actual: {inj.actual_response}
                                  </p>
                                )}
                              </div>

                              {/* Action Buttons */}
                              <div className="flex items-center space-x-1 shrink-0">
                                {injStatus === 'pending' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateInjectionStatus(exercise.id, inj.id, 'sent');
                                    }}
                                    data-testid={`mark-sent-${inj.id || idx}`}
                                  >
                                    <Clock className="h-3 w-3 mr-1" />
                                    Pending
                                  </Button>
                                )}
                                {injStatus === 'sent' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 border-green-500/40 text-green-400 hover:bg-green-500/10"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateInjectionStatus(exercise.id, inj.id, 'completed');
                                    }}
                                    data-testid={`mark-complete-${inj.id || idx}`}
                                  >
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Actioned
                                  </Button>
                                )}
                                {injStatus === 'completed' && (
                                  <Badge className="bg-green-500/20 text-green-400 border border-green-500/30 text-xs">
                                    Complete
                                  </Badge>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ExConDashboard;
